import {
  MAX_RENT_AMOUNT,
  MIN_RENT_AMOUNT,
  MOVE_IN_COSTS_PAYMENT_MIN_AMOUNT,
  ONE_TIME_PAYMENT_MIN_AMOUNT,
} from '@rental-manager/constants/PaymentConstants';

const errorMessages = {
  /* When an input is valid, set the error field to an empty string. */
  CLEAR_ERROR: '',

  /* Payment Settings Page */
  MONTHLY_RENT_EMPTY: 'Please enter the monthly rent.',
  MONTHLY_RENT_INVALID: `Monthly rent should be between $${MIN_RENT_AMOUNT / 100} and $${MAX_RENT_AMOUNT / 100}.`,
  FIRST_PAYMENT_DUE_DATE_EMPTY: 'Please pick the first payment date.',
  FIRST_PAYMENT_BEFORE_GRACE_PERIOD: 'Please select a future date.',

  ONBOARDING_V2_DUE_DATE_REQUIRED: 'Please select the payment date.',

  /* Account Info Page */
  ROUTING_NUMBER_EMPTY: 'Please enter your routing number.',
  ROUTING_NUMBER_INVALID: 'Routing number must be a 9-digit number.',
  ROUTING_NUMBER_DEFAULT_ERROR: 'Please enter a valid routing number.',
  CHECKING_ACCOUNT_NUMBER_EMPTY: 'Please enter your checking account number.',
  CHECKING_ACCOUNT_NUMBER_INVALID: 'Account number must be 12-digit number.',
  CHECKING_ACCOUNT_NUMBER_CONFIRMATION_EMPTY: 'Please confirm your checking account number.',
  CHECKING_ACCOUNT_NUMBER_CONFIRMATION_NOT_MATCH: 'Account numbers don’t match.',

  /* Identity Verification Page */
  LEGAL_FIRST_NAME_EMPTY: 'Please enter your legal first name.',
  LAST_NAME_EMPTY: 'Please enter your last name.',
  STREET_EMPTY: 'Please enter your street address.',
  CITY_EMPTY: 'Please enter your address city.',
  STATE_EMPTY: 'Please select your address state.',
  ZIP_CODE_EMPTY: 'Please enter 5 digit ZIP code.',
  ZIP_CODE_INVALID: 'Zip code invalid.',
  PHONE_NUMBER_INVALID: 'Please enter a valid, 10-digit US phone number.',
  MONTH_OF_BIRTH_EMPTY: 'Please select the month of your birth.',
  DAY_OF_BIRTH_EMPTY: 'Please enter the day of your birth.',
  DAY_OF_BIRTH_INVALID: 'Invalid day of birth.',
  YEAR_OF_BIRTH_EMPTY: 'Please enter the year of your birth.',
  YEAR_OF_BIRTH_INVALID: 'Invalid year of birth.',
  BIRTH_DATE_INVALID: 'You must be 18 years of age or older.',
  EMAIL_ADDRESS_EMPTY: 'Please enter your email address.',
  INVALID_EMAIL_ADDRESS: 'Please enter a valid email address.',
  UNIQUE_EMAIL_ADDRESS: 'Tenant and landlord email addresses cannot match.',
  CANNOT_INVITE_EXISTING_RENTER:
    'This tenant is already set up for online payments. To ' +
    'extend their rent term, edit the payment end date from your Payments dashboard.',
  SSN_LAST4_INVALID: 'Please enter the last 4 digits of your Social Security Number.',
  BUSINESS_NAME_EMPTY: 'Please enter your business name.',
  EMPLOYER_IDENTIFICATION_NUMBER_INVALID: 'Please provide a 9-digit number.',
  BUSINESS_STREET_EMPTY: 'Please enter your business street address.',
  BUSINESS_CITY_EMPTY: 'Please enter your business address city.',
  BUSINESS_STATE_EMPTY: 'Please select your business address state.',
  FULL_SSN_INVALID: 'Please provide a 9-digit number.',

  /* Phone Verification Page */
  PHONE_VERIFICATION_INVALID: 'Please enter a 5-digit verification code.',
  PHONE_VERIFICATION_INCORRECT: 'The code is incorrect. Try again.',
  PHONE_VERIFICATION_TOO_MANY_ATTEMPTS: 'Too many attempts have been made. Please contact support to continue.',

  /* Invite Tenant Page */
  TENANT_FIRST_NAME_EMPTY: "Please enter your tenant's first name.",
  TENANT_LAST_NAME_EMPTY: "Please enter your tenant's last name.",
  TENANT_EMAIL_ADDRESS_EMPTY: "Please enter your tenant's email address.",

  /* Invite Tenant Sign and collect variation */
  TENANT_NOT_SELECTED: 'Please select your tenant.',

  /* Edit Payment Length Page */
  NEW_END_DATE_MATCHES_PREVIOUS: 'Your payment end date is already set to this value.',

  /* Edit Rent Amount Page */
  NEW_RENT_AMOUNT_MATCHES_PREVIOUS: 'The rent amount is already set to this value.',

  /* One Time Payment Page */
  ONE_TIME_PAYMENT_NOTE_MAX_LENGTH: "45/45 - You've reached the maximum number of characters.",
  ONE_TIME_PAYMENT_TYPE_EMPTY: 'Please select what the payment is for.',
  ONE_TIME_PAYMENT_TYPE_INVALID: 'This payment type is invalid.',
  ONE_TIME_PAYMENT_AMOUNT_EMPTY: 'Please enter the amount.',
  ONE_TIME_PAYMENT_NOTES_EMPTY: 'Please enter a note.',
  ONE_TIME_PAYMENT_MAX_AMOUNT: (maxAmount) => `This amount can't exceed ${maxAmount}. Contact us for more info.`,
  ONE_TIME_PAYMENT_MIN_AMOUNT: `This amount can't be less than $${
    ONE_TIME_PAYMENT_MIN_AMOUNT / 100
  }. Contact us for more info.`,

  /* Move In Costs Page */
  MOVE_IN_COSTS_NOTE_MAX_LENGTH: "45/45 - You've reached the maximum number of characters.",
  MOVE_IN_COSTS_FOR_EMPTY: 'Please enter what this move-in cost is for.',
  MOVE_IN_COSTS_PAYMENT_AMOUNT_EMPTY: 'Please enter the amount.',
  MOVE_IN_COSTS_PAYMENT_MAX_AMOUNT: (maxAmount) => `This amount can't exceed ${maxAmount}. Contact us for more info.`,
  MOVE_IN_COSTS_PAYMENT_MIN_AMOUNT: `This amount can't be less than $${
    MOVE_IN_COSTS_PAYMENT_MIN_AMOUNT / 100
  }. Contact us for more info.`,
  MOVE_IN_COSTS_DUE_DATE_EMPTY: 'Please select a payment date.',
  MOVE_IN_COSTS_BEFORE_START_DATE: 'Please select a future date.',
  MOVE_IN_COSTS_AFTER_CUTOFF_DATE: (cutoffDate) => `Please select a date before ${cutoffDate}.`,

  /* Renter Payment info */
  ACCOUNT_NUMBER_CONFIRM_ACCOUNT_NO_MATCH: 'Account number and Confirm account number do not match.',
  ACCOUNT_NUMBER_EMPTY: 'Enter Account number.',
  CONFIRM_ACCOUNT_NUMBER_EMPTY: 'Enter Account number again to confirm.',
  PAYMENT_CARD_NAME_EMPTY: 'Please enter the name that appears on your card.',
  PAYMENT_CARD_NUMBER_INVALID: 'Please enter the name that appears on your card.',
  PAYMENT_BILLING_STREET_ADDRESS: 'Please enter your billing address.',
  PAYMENT_ADDRESS_CITY_EMPTY: 'Please fill in your city.',
  PAYMENT_STATE_EMPTY: 'Please select the state for your billing address.',
  PAYMENT_ZIP_CODE_EMPTY: 'Please fill in your zip code.',
  PAYMENT_INVALID_TYPE_CARD: 'We only accept Visa, MasterCard, American Express and Discover cards.',
  PAYMENT_TYPE_NOT_VALID: "At this time we only accept debit and credit cards. We don't accept prepaid or other cards.",
  PAYMENT_TYPE_MISMATCH: (cardType) => `The card information you have entered is not a ${cardType} card`,

  /* Microdeposits page */
  MICRODEPOSIT_AMOUNT_EMPTY: 'Please fill in a value.',

  /* Payment Plan Installment Requests Page */
  PAYMENT_PLAN_NOTE_MAX_LENGTH: "45/45 - You've reached the maximum number of characters.",
  PAYMENT_PLAN_AMOUNT_EMPTY: 'Please enter the amount.',
  PAYMENT_PLAN_NOTES_EMPTY: 'Please enter a note.',
  PAYMENT_PLAN_MAX_AMOUNT: (maxAmount) => `This amount can't exceed ${maxAmount}. Contact us for more info.`,
  PAYMENT_PLAN_MIN_AMOUNT: (minAmount) => `This amount can't be less than ${minAmount}. Contact us for more info.`,

  /* Rent reporting */
  COMBINED_NAMES_ERROR_TEXT: 'Multiple names detected. Enter only one name for your primary tenant.',
  COMBINED_NAMES_ERROR_TEXT_CONFIRM:
    'Multiple names detected. Review and correct the name your landlord provided if needed.',
  COMBINED_NAMES_ERROR_TEXT_RENTER: 'Multiple names detected. Enter only one name.',
  FIRST_NAME_EMPTY: 'Please enter your first name.',
};

export default errorMessages;
